import React, { useState, useEffect } from 'react';
import { Star, Quote } from 'lucide-react';
import Card from '../ui/Card';
import { testimonials as fallbackTestimonials } from '../../data/mockData';
import { testimonialAPI, BASE_URL } from '../../services/api';

const DEFAULT_AVATAR = 'https://ui-avatars.com/api/?background=006efa&color=fff&name=';

const getAvatarUrl = (image, name) => {
  if (!image) return `${DEFAULT_AVATAR}${encodeURIComponent(name || 'U')}`;
  return image.startsWith('http') ? image : `${BASE_URL}${image}`;
};

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState(fallbackTestimonials);

  useEffect(() => {
    testimonialAPI.getActive()
      .then(res => {
        const data = res.data.data;
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data.map(t => ({
            id: t.id,
            name: t.customer_name,
            role: t.role,
            company: t.company,
            image: t.image_url,
            rating: t.rating,
            text: t.text,
          })));
        }
      })
      .catch(() => {
        // Keep the fallback testimonials if the API is unreachable
      });
  }, []);

  return (
    <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What Our Customers Say
          </h2>
          <p className="text-lg text-gray-600">
            Trusted by thousands of businesses across India
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <Card
              key={testimonial.id}
              hover
              className="relative animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 text-primary-200">
                <Quote className="w-12 h-12" />
              </div>

              <div className="space-y-4">
                {/* Rating */}
                <div className="flex items-center space-x-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-yellow-400 fill-current"
                    />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-700 leading-relaxed text-lg">
                  "{testimonial.text}"
                </p>

                {/* Author Info */}
                <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                  <img
                    src={getAvatarUrl(testimonial.image, testimonial.name)}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover ring-4 ring-white shadow-lg"
                    onError={e => { e.target.src = `${DEFAULT_AVATAR}${encodeURIComponent(testimonial.name || 'U')}`; }}
                  />
                  <div>
                    <div className="font-bold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.role}
                    </div>
                    <div className="text-sm text-primary-600 font-medium">
                      {testimonial.company}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
